import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Gavel, Plus, ArrowRight, ArrowLeft, ChevronRight, CheckCircle2, X,
  Clock, Loader2, Trash2, Flag,
} from "lucide-react";

export default function DisputesPage() {
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/disputes?status=${status}`),
        api.get("/disputes/stages"),
      ]);
      setItems(a.data || []);
      setStages(b.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div data-testid="disputes-page">
      <PageHeader
        eyebrow="La Résolution"
        title="Disputes"
        description="Walk a dispute through structured pre-litigation stages before court — most matters resolve before the last step."
        action={
          <Button onClick={() => setAdding(true)} className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="new-dispute-btn">
            <Plus size={14} className="mr-2" /> New dispute
          </Button>
        }
      />

      <StagesStrip stages={stages} />

      <div className="flex items-center gap-2 mt-6 mb-4">
        {["open", "resolved", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${status === s ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0]"}`}
            data-testid={`disputes-filter-${s}`}
          >{s}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#4A5568] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Gavel} title="No disputes yet" description="Create a dispute to track a problem through negotiation, formal notice, mediation and (only if needed) court."
          action={<Button onClick={() => setAdding(true)} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs"><Plus size={14} className="mr-2" /> New dispute</Button>}
        />
      ) : (
        <div className="space-y-3">
          {items.map((d) => <DisputeRow key={d.id} d={d} stages={stages} />)}
        </div>
      )}

      {adding && <NewDisputeModal stages={stages} onClose={() => setAdding(false)} onCreated={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function StagesStrip({ stages }) {
  if (!stages.length) return null;
  return (
    <div className="border border-[#E8E3F0] bg-white overflow-x-auto">
      <div className="flex min-w-max">
        {stages.map((s, i) => (
          <div key={s.slug} className="px-5 py-4 border-r border-[#E8E3F0] last:border-r-0 min-w-[180px]">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 border border-[#2A0F5A] text-[10px] flex items-center justify-center font-mono">{s.order}</div>
              <div className="label-eyebrow text-[#C5A059]">{s.typical_days ? `~${s.typical_days}d` : "—"}</div>
            </div>
            <div className="font-serif text-base leading-tight">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DisputeRow({ d, stages }) {
  const idx = stages.findIndex((s) => s.slug === d.stage);
  const stageMeta = stages.find((s) => s.slug === d.stage);
  return (
    <Link
      to={`/app/disputes/${d.id}`}
      className="block border border-[#E8E3F0] bg-white p-5 hover:border-[#2A0F5A]"
      data-testid={`dispute-row-${d.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow mb-1">{d.counterparty || "—"}</div>
          <h3 className="font-serif text-xl leading-tight">{d.title}</h3>
          {d.summary && <p className="text-sm text-[#4A5568] mt-1 line-clamp-1">{d.summary}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge tone={d.status === "resolved" ? "success" : "warn"}>
            {d.status === "resolved" ? `Resolved · ${d.outcome}` : stageMeta?.label}
          </StatusBadge>
          {d.stage_due_date && d.status !== "resolved" && (
            <div className="text-xs text-[#4A5568] flex items-center gap-1"><Clock size={11} /> {d.stage_due_date}</div>
          )}
        </div>
      </div>

      {/* Progress rail */}
      {d.status !== "resolved" && (
        <div className="flex gap-1">
          {stages.map((s, i) => (
            <div
              key={s.slug}
              className={`h-1 flex-1 ${i <= idx ? "bg-[#2A0F5A]" : "bg-[#E8E3F0]"}`}
              title={s.label}
            />
          ))}
        </div>
      )}
    </Link>
  );
}

function NewDisputeModal({ stages, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", counterparty: "", summary: "", stage: "direct_negotiation",
    amount_at_stake: "", jurisdiction: "Generic", case_id: "",
  });
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/cases").then((r) => setCases(r.data || [])); }, []);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = { ...form, case_id: form.case_id || null };
      await api.post("/disputes", payload);
      toast.success("Dispute created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#0B132B]/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="new-dispute-modal">
      <div className="bg-white max-w-2xl w-full mt-16 border border-[#E8E3F0]">
        <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
          <h2 className="font-serif text-2xl">New dispute</h2>
          <button onClick={onClose} className="text-[#4A5568] hover:text-[#2A0F5A]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short title" className="rounded-none border-[#E8E3F0]" data-testid="dispute-title-input" />
          <Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} placeholder="Counterparty" className="rounded-none border-[#E8E3F0]" />
          <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Short summary of the dispute" rows={3} className="rounded-none border-[#E8E3F0]" />
          <div className="grid grid-cols-2 gap-4">
            <Input value={form.amount_at_stake} onChange={(e) => setForm({ ...form, amount_at_stake: e.target.value })} placeholder="Amount at stake (optional)" className="rounded-none border-[#E8E3F0]" />
            <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="border border-[#E8E3F0] px-3 py-2 text-sm bg-white" data-testid="dispute-stage-select">
              {stages.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            </select>
          </div>
          {cases.length > 0 && (
            <select value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })} className="w-full border border-[#E8E3F0] px-3 py-2 text-sm bg-white">
              <option value="">— Link to a case (optional) —</option>
              {cases.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E8E3F0] flex justify-end gap-3 bg-[#FAFAF8]">
          <Button variant="outline" onClick={onClose} className="rounded-none border-[#E8E3F0]">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="submit-dispute-btn">
            {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Creating…</> : "Create dispute"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Detail --------------------------------------------------------
export function DisputeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [stages, setStages] = useState([]);
  const [advancing, setAdvancing] = useState(false);
  const [note, setNote] = useState("");
  const [activityForm, setActivityForm] = useState({ title: "", body: "", date: "" });
  const [resolveForm, setResolveForm] = useState({ outcome: "settled", notes: "" });
  const [showResolve, setShowResolve] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/disputes/${id}`),
      api.get("/disputes/stages"),
    ]);
    setD(a.data); setStages(b.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const advance = async (toStage) => {
    setAdvancing(true);
    try {
      await api.post(`/disputes/${id}/advance`, { to_stage: toStage || null, note });
      setNote(""); await load();
      toast.success("Advanced");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not advance"); }
    finally { setAdvancing(false); }
  };

  const logActivity = async () => {
    if (!activityForm.title.trim()) return toast.error("Title required");
    await api.post(`/disputes/${id}/activity`, activityForm);
    setActivityForm({ title: "", body: "", date: "" });
    await load();
    toast.success("Logged");
  };

  const resolve = async () => {
    await api.post(`/disputes/${id}/resolve`, resolveForm);
    setShowResolve(false);
    await load();
    toast.success("Resolved");
  };

  const removeDispute = async () => {
    if (!window.confirm("Delete this dispute?")) return;
    await api.delete(`/disputes/${id}`);
    toast.success("Deleted");
    navigate("/app/disputes");
  };

  if (!d) return <div className="text-sm text-[#4A5568] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  const current = stages.find((s) => s.slug === d.stage);
  const nextIdx = stages.findIndex((s) => s.slug === d.stage) + 1;
  const nextStage = stages[nextIdx] || null;

  return (
    <div data-testid="dispute-detail-page">
      <Link to="/app/disputes" className="label-eyebrow inline-flex items-center gap-1.5 mb-4 hover:text-[#2A0F5A]">
        <ArrowLeft size={12} /> All disputes
      </Link>
      <PageHeader
        eyebrow={d.counterparty || "Dispute"}
        title={d.title}
        description={d.summary}
        action={
          <div className="flex items-center gap-2">
            <StatusBadge tone={d.status === "resolved" ? "success" : "warn"}>
              {d.status === "resolved" ? `Resolved · ${d.outcome}` : current?.label}
            </StatusBadge>
            {d.status !== "resolved" && (
              <Button onClick={() => setShowResolve(true)} variant="outline" className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs" data-testid="resolve-btn">
                <Flag size={14} className="mr-2" /> Resolve
              </Button>
            )}
            <Button onClick={removeDispute} variant="outline" className="rounded-none border-[#E8E3F0] hover:border-[#8a2121] hover:text-[#8a2121] uppercase tracking-widest text-xs">
              <Trash2 size={14} />
            </Button>
          </div>
        }
      />

      {/* Current stage card + playbook */}
      {current && d.status !== "resolved" && (
        <div className="border border-[#E8E3F0] bg-white p-6 mb-6" data-testid="current-stage-card">
          <div className="flex items-start justify-between mb-3 gap-3">
            <div>
              <div className="label-eyebrow mb-1">Current stage · {current.order} of {stages.length}</div>
              <div className="font-serif text-2xl">{current.label}</div>
            </div>
            {d.stage_due_date && (
              <div className="text-xs text-[#4A5568] flex items-center gap-1.5">
                <Clock size={11} /> Review by {d.stage_due_date}
              </div>
            )}
          </div>
          <p className="text-sm text-[#2A0F5A]/85 mb-4">{current.description}</p>
          <ul className="space-y-1.5 text-sm mb-5">
            {(current.actions || []).map((a, i) => (
              <li key={i} className="flex gap-2">
                <ChevronRight size={14} className="text-[#C5A059] mt-0.5 shrink-0" />{a}
              </li>
            ))}
          </ul>
          {nextStage && (
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#E8E3F0]">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note (e.g. 'ignored our email')"
                className="rounded-none border-[#E8E3F0] flex-1 min-w-[200px]"
              />
              <Button onClick={() => advance(null)} disabled={advancing} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="advance-btn">
                Advance to {nextStage.label} <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Add activity */}
      {d.status !== "resolved" && (
        <div className="border border-[#E8E3F0] bg-white p-5 mb-6">
          <div className="label-eyebrow mb-3">Log an activity</div>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <Input value={activityForm.title} onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })} placeholder="Title (e.g. 'Mediator call')" className="rounded-none border-[#E8E3F0] sm:col-span-2" data-testid="activity-title-input" />
            <Input type="date" value={activityForm.date} onChange={(e) => setActivityForm({ ...activityForm, date: e.target.value })} className="rounded-none border-[#E8E3F0]" />
          </div>
          <Textarea value={activityForm.body} onChange={(e) => setActivityForm({ ...activityForm, body: e.target.value })} placeholder="Notes" rows={2} className="rounded-none border-[#E8E3F0] mb-3" />
          <Button onClick={logActivity} variant="outline" className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs" data-testid="log-activity-btn">
            <Plus size={14} className="mr-2" /> Log
          </Button>
        </div>
      )}

      {/* Timeline (events + activity merged, newest first) */}
      <div className="border border-[#E8E3F0] bg-white">
        <div className="px-5 py-3 border-b border-[#E8E3F0] label-eyebrow">Timeline</div>
        <div className="divide-y divide-[#E8E3F0]">
          {[...(d.activity || []).map((a) => ({ ...a, _kind: "activity" })),
             ...(d.events || []).map((e) => ({ ...e, _kind: "event" }))]
            .sort((a, b) => (b.at || b.created_at || "").localeCompare(a.at || a.created_at || ""))
            .map((it, i) => (
              <div key={it.id || i} className="px-5 py-3 flex items-start gap-3">
                <div className="text-xs text-[#4A5568] min-w-[120px]">
                  {(it.at || it.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
                <div className="flex-1 min-w-0">
                  {it._kind === "event" ? (
                    <>
                      <div className="label-eyebrow">{it.type?.replace("dispute.", "").replace(/_/g, " ")}</div>
                      <div className="text-sm">
                        {it.from_stage && it.to_stage && <>→ <b>{stages.find((s) => s.slug === it.to_stage)?.label || it.to_stage}</b></>}
                        {it.outcome && <>Outcome: <b>{it.outcome}</b></>}
                        {it.note && <div className="text-xs text-[#4A5568] mt-0.5">"{it.note}"</div>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-serif text-base leading-tight">{it.title}</div>
                      {it.body && <p className="text-sm text-[#2A0F5A]/85 whitespace-pre-wrap mt-1">{it.body}</p>}
                    </>
                  )}
                </div>
              </div>
            ))}
          {(!d.events || d.events.length === 0) && (!d.activity || d.activity.length === 0) && (
            <div className="px-5 py-5 text-xs text-[#4A5568]">No events yet.</div>
          )}
        </div>
      </div>

      {/* Resolve modal */}
      {showResolve && (
        <div className="fixed inset-0 bg-[#0B132B]/40 backdrop-blur-sm z-50 flex items-start justify-center p-4" data-testid="resolve-modal">
          <div className="bg-white max-w-md w-full mt-20 border border-[#E8E3F0]">
            <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
              <h3 className="font-serif text-xl">Resolve dispute</h3>
              <button onClick={() => setShowResolve(false)}><X size={16} /></button>
            </div>
            <div className="p-6 space-y-3">
              <select value={resolveForm.outcome} onChange={(e) => setResolveForm({ ...resolveForm, outcome: e.target.value })} className="w-full border border-[#E8E3F0] px-3 py-2 text-sm bg-white" data-testid="resolve-outcome">
                {["settled", "won", "lost", "withdrawn", "abandoned"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <Textarea value={resolveForm.notes} onChange={(e) => setResolveForm({ ...resolveForm, notes: e.target.value })} rows={3} placeholder="Notes" className="rounded-none border-[#E8E3F0]" />
            </div>
            <div className="px-6 py-4 border-t border-[#E8E3F0] flex justify-end gap-2 bg-[#FAFAF8]">
              <Button variant="outline" onClick={() => setShowResolve(false)} className="rounded-none border-[#E8E3F0]">Cancel</Button>
              <Button onClick={resolve} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="confirm-resolve-btn">Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
